import type { Appearance } from '@sunucupark/design-tokens/appearance';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_UNDO_SEND_SECONDS } from '@/features/compose';
import { DEFAULT_APPEARANCE } from '../appearance';
import type { RibbonMode } from '../chrome/types';
import type { Language } from '../i18n';
import type { ColorScheme } from '../theme';

/** How many past searches the panel keeps; more than this is a history page, not a hint. */
export const RECENT_SEARCHES = 8;

export interface PreferencesState {
  /** `system` follows the operating system. */
  colorScheme: ColorScheme;
  language: Language;
  /** Whether single-key shortcuts are active; the help dialog says what they are. */
  shortcutsEnabled: boolean;
  /**
   * What this person chose about the look — the greys, the accent, the font,
   * the size and the corners. The brand still owns the name and the logo
   * (ADR 0009 §8); everything in here is theirs to change.
   */
  appearance: Appearance;
  /**
   * How long a sent message waits in the tray before it really goes, in
   * seconds; `0` sends at once. The gateway holds it, so it works even with
   * the browser closed (GAP D6).
   */
  undoSendSeconds: number;
  /**
   * How the classic theme's command bar is drawn: one row of commands, or
   * the grouped rows with captions. Ignored by the other theme, which has no
   * command bar at all.
   */
  ribbonMode: RibbonMode;
  /**
   * The paper a message body sits on: `auto` follows the application's theme
   * only as far as light does — mail is written for a light background — and
   * `dark` is the reader saying they would rather have it dark anyway.
   */
  readingPaper: 'light' | 'dark';
  setColorScheme: (scheme: ColorScheme) => void;
  setLanguage: (language: Language) => void;
  setShortcutsEnabled: (enabled: boolean) => void;
  setAppearance: (appearance: Partial<Appearance>) => void;
  resetAppearance: () => void;
  setUndoSendSeconds: (seconds: number) => void;
  setRibbonMode: (mode: RibbonMode) => void;
  setReadingPaper: (paper: 'light' | 'dark') => void;
  /** What was searched for last, newest first; the search panel offers them again. */
  recentSearches: string[];
  /** The account those searches belong to: another account on this browser never sees them. */
  recentSearchesOf: string | null;
  rememberSearch: (accountId: string, query: string) => void;
  /** Drops the remembered searches (sign-out). */
  forgetSearches: () => void;
}

/**
 * What the reader chose about the application itself. Kept apart from the
 * layout store because these follow the person, not the window: phase 3 moves
 * them to the server so the phone and the browser agree.
 */
export const usePreferences = create<PreferencesState>()(
  persist(
    (set) => ({
      colorScheme: 'system',
      language: 'tr',
      shortcutsEnabled: true,
      appearance: DEFAULT_APPEARANCE,
      undoSendSeconds: DEFAULT_UNDO_SEND_SECONDS,
      ribbonMode: 'simplified',
      readingPaper: 'light',
      recentSearches: [],
      recentSearchesOf: null,
      setColorScheme: (colorScheme) => set({ colorScheme }),
      setLanguage: (language) => set({ language }),
      setShortcutsEnabled: (shortcutsEnabled) => set({ shortcutsEnabled }),
      setAppearance: (appearance) =>
        set((state) => ({ appearance: { ...state.appearance, ...appearance } })),
      resetAppearance: () => set({ appearance: DEFAULT_APPEARANCE }),
      setUndoSendSeconds: (undoSendSeconds) => set({ undoSendSeconds }),
      setRibbonMode: (ribbonMode) => set({ ribbonMode }),
      setReadingPaper: (readingPaper) => set({ readingPaper }),
      rememberSearch: (accountId, query) =>
        set((state) => {
          const trimmed = query.trim();
          if (!trimmed || !accountId) return state;
          const mine = state.recentSearchesOf === accountId ? state.recentSearches : [];
          return {
            recentSearches: [trimmed, ...mine.filter((item) => item !== trimmed)].slice(0, RECENT_SEARCHES),
            recentSearchesOf: accountId,
          };
        }),
      forgetSearches: () => set({ recentSearches: [], recentSearchesOf: null }),
    }),
    {
      name: 'web.preferences',
      // 7: searches are kept per account; a list stored before that belongs to nobody and is dropped.
      version: 7,
      // Older stores lack the newer preferences; the defaults fill them in.
      migrate: (persisted) =>
        ({
          appearance: DEFAULT_APPEARANCE,
          undoSendSeconds: DEFAULT_UNDO_SEND_SECONDS,
          ribbonMode: 'simplified' as RibbonMode,
          readingPaper: 'light' as const,
          ...(persisted as object),
          recentSearches: (persisted as { recentSearchesOf?: unknown }).recentSearchesOf
            ? (persisted as { recentSearches: string[] }).recentSearches
            : ([] as string[]),
          recentSearchesOf: ((persisted as { recentSearchesOf?: string | null }).recentSearchesOf ?? null),
        }) as never,
    },
  ),
);
